/**
 * AudioUploadModal — two-phase audio collection inside the new-draft modal.
 *
 * Phase 1 ("select"): the user picks 1–10 audio files (same MIME). Clicking
 * "繼續" hands them to `onMergeFiles` (server concats with 0.3s silence) and
 * we transition to phase 2.
 *
 * Phase 2 ("range"): the merged audio is shown in AudioRangeSelector with
 * multi-region support. "下一步" calls `onSubmit` with the picked ranges.
 */

import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ModalShell from './ModalShell';
import MultiAudioFileList from '../../MultiAudioFileList';
import AudioRangeSelector, { type AudioRange } from '../../AudioRangeSelector';
import { resolveMediaUrl } from '../../../../utils/mediaUrl';
import './AudioUploadModal.css';

interface AudioUploadModalProps {
  initialFiles?: File[];
  initialMergedUrl?: string;
  initialMergedDuration?: number;
  initialRanges?: AudioRange[];
  onMergeFiles: (files: File[]) => Promise<{ mergedAudioUrl: string; durationSeconds: number }>;
  onSubmit: (files: File[], mergedUrl: string, mergedDuration: number, ranges: AudioRange[]) => void;
  onBack: () => void;
  onClose: () => void;
}

type Phase = 'select' | 'range';

const AudioUploadModal: React.FC<AudioUploadModalProps> = ({
  initialFiles,
  initialMergedUrl,
  initialMergedDuration,
  initialRanges,
  onMergeFiles,
  onSubmit,
  onBack,
  onClose,
}) => {
  const { t } = useTranslation(['creator', 'common']);

  const [phase, setPhase] = useState<Phase>(initialMergedUrl ? 'range' : 'select');
  const [files, setFiles] = useState<File[]>(initialFiles ?? []);
  const [mergedUrl, setMergedUrl] = useState<string | null>(initialMergedUrl ?? null);
  const [mergedDuration, setMergedDuration] = useState<number>(initialMergedDuration ?? 0);
  const [ranges, setRanges] = useState<AudioRange[]>(initialRanges ?? []);
  const [merging, setMerging] = useState(false);
  const [mergeError, setMergeError] = useState<string | null>(null);

  const handleMerge = useCallback(async () => {
    if (files.length === 0) return;
    setMerging(true);
    setMergeError(null);
    try {
      const result = await onMergeFiles(files);
      setMergedUrl(result.mergedAudioUrl);
      setMergedDuration(result.durationSeconds);
      // Reset ranges when re-merging — fresh start for the new audio
      setRanges([]);
      setPhase('range');
    } catch (e: any) {
      const msg = e?.response?.data?.message || e?.message || 'Failed to merge audio';
      setMergeError(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setMerging(false);
    }
  }, [files, onMergeFiles]);

  const handleSubmitRanges = useCallback(() => {
    if (!mergedUrl) return;
    onSubmit(files, mergedUrl, mergedDuration, ranges);
  }, [files, mergedUrl, mergedDuration, ranges, onSubmit]);

  // Resolve mergedUrl through media-url helper so dev/prod both work
  const playableMergedUrl = mergedUrl ? resolveMediaUrl(mergedUrl) : '';

  if (phase === 'select') {
    return (
      <ModalShell
        title={t('creator:workflow.resourceModal.audioTitle')}
        onClose={onClose}
        onBack={onBack}
        onNext={handleMerge}
        nextDisabled={files.length === 0 || merging}
        nextLabel={merging ? t('creator:workflow.audioUpload.merging') : t('creator:workflow.audioUpload.continueToRange')}
        wide
      >
        <MultiAudioFileList
          files={files}
          onChange={setFiles}
          disabled={merging}
        />

        {mergeError && (
          <div className="multi-audio-error" role="alert" style={{ marginTop: '0.75rem' }}>
            {mergeError}
          </div>
        )}

        {merging && (
          <div className="audio-merge-status" role="status" aria-live="polite">
            <span className="loading-spinner" />
            <span>{t('creator:workflow.audioUpload.merging')}</span>
          </div>
        )}
      </ModalShell>
    );
  }

  // phase === 'range'
  return (
    <ModalShell
      title={t('creator:workflow.resourceModal.audioTitle')}
      onClose={onClose}
      onBack={() => setPhase('select')}
      backLabel={t('creator:workflow.audioUpload.backToFiles')}
      onNext={handleSubmitRanges}
      nextDisabled={!mergedUrl || ranges.length === 0}
      wide
    >
      <div className="audio-range-wrapper">
        <AudioRangeSelector
          audioUrl={playableMergedUrl}
          duration={mergedDuration}
          onRangesChange={setRanges}
          onDurationLoaded={(d) => setMergedDuration(d)}
          initialRanges={ranges.length > 0 ? ranges : undefined}
        />
      </div>
    </ModalShell>
  );
};

export default AudioUploadModal;
